// src/components/personal/sections/OrdersSection/utils/statusHelpers.ts
import { useTranslation } from 'react-i18next';
import { COLORS } from '@/theme';
import { OrderStatus, PaymentStatus, SalesChannel } from '../types/types';

/**
 * Получение цветовых классов для статуса заказа
 */
export const getOrderStatusColor = (status: OrderStatus): string => {
  switch (status) {
    case 'abandoned': return COLORS.status.warning.bg + ' ' + COLORS.status.warning.text;
    case 'new': return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
    // Используем info или success для отсутствующих статусов
    case 'processing': return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
    case 'delivering': return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
    case 'completed': return COLORS.status.success.bg + ' ' + COLORS.status.success.text;
    case 'cancelled': return COLORS.status.error.bg + ' ' + COLORS.status.error.text;
    default: return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
  }
};

/**
 * Получение цветовых классов для статуса платежа
 */
export const getPaymentStatusColor = (status: PaymentStatus): string => {
  switch (status) {
    case 'pending': return COLORS.status.warning.bg + ' ' + COLORS.status.warning.text;
    case 'paid': return COLORS.status.success.bg + ' ' + COLORS.status.success.text;
    case 'error': return COLORS.status.error.bg + ' ' + COLORS.status.error.text;
    case 'refund': return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
    default: return COLORS.status.info.bg + ' ' + COLORS.status.info.text;
  }
};

/**
 * Получение стиля бейджа для статуса
 * Мы должны использовать стили из темы, но при этом справиться с проблемой отсутствующих ключей
 */
export const getOrderStatusBadgeStyle = (status: OrderStatus): string => {
  // Используем базовый стиль бейджа и добавляем цветовой класс в зависимости от статуса
  const baseStyle = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  
  switch (status) {
    case 'abandoned': return `${baseStyle} ${COLORS.status.warning.bg} ${COLORS.status.warning.text}`;
    case 'new': return `${baseStyle} ${COLORS.status.info.bg} ${COLORS.status.info.text}`;
    case 'processing': return `${baseStyle} ${COLORS.status.info.bg} ${COLORS.status.info.text}`;
    case 'delivering': return `${baseStyle} ${COLORS.status.info.bg} ${COLORS.status.info.text}`;
    case 'completed': return `${baseStyle} ${COLORS.status.success.bg} ${COLORS.status.success.text}`;
    case 'cancelled': return `${baseStyle} ${COLORS.status.error.bg} ${COLORS.status.error.text}`;
    default: return `${baseStyle} ${COLORS.status.info.bg} ${COLORS.status.info.text}`;
  }
};

/**
 * Получение текстового представления статуса заказа с поддержкой i18n
 */
export const getOrderStatusText = (status: OrderStatus): string => {
  const { t } = useTranslation(['orders']);
  
  switch (status) {
    case 'abandoned': return t('status.abandoned');
    case 'new': return t('status.new');
    case 'processing': return t('status.processing');
    case 'delivering': return t('status.delivering');
    case 'completed': return t('status.completed');
    case 'cancelled': return t('status.cancelled');
    default: return t('status.new');
  }
};

/**
 * Получение текстового представления статуса платежа с поддержкой i18n
 */
export const getPaymentStatusText = (status: PaymentStatus): string => {
  const { t } = useTranslation(['orders']);
  
  switch (status) {
    case 'pending': return t('payment_status.pending');
    case 'paid': return t('payment_status.paid');
    case 'error': return t('payment_status.error');
    case 'refund': return t('payment_status.refund');
    default: return t('payment_status.pending');
  }
};

/**
 * Получение текстового представления канала продаж с поддержкой i18n
 */
export const getSalesChannelText = (channel: SalesChannel): string => {
  const { t } = useTranslation(['orders']);
  
  switch (channel) {
    case 'whatsapp': return t('sales_channel.whatsapp');
    case 'instagram': return t('sales_channel.instagram');
    case 'meta_messages': return t('sales_channel.meta_messages');
    case 'telegram': return t('sales_channel.telegram');
    case 'website': return t('sales_channel.website');
    default: return t('sales_channel.whatsapp');
  }
};

/**
 * Статичные варианты функций получения текста для использования вне компонентов
 */
export const getOrderStatusTextStatic = (status: OrderStatus, t: (key: string) => string): string => {
  switch (status) {
    case 'abandoned': return t('status.abandoned');
    case 'new': return t('status.new');
    case 'processing': return t('status.processing');
    case 'delivering': return t('status.delivering');
    case 'completed': return t('status.completed');
    case 'cancelled': return t('status.cancelled');
    default: return t('status.new');
  }
};

export const getPaymentStatusTextStatic = (status: PaymentStatus, t: (key: string) => string): string => {
  switch (status) {
    case 'pending': return t('payment_status.pending');
    case 'paid': return t('payment_status.paid');
    case 'error': return t('payment_status.error');
    case 'refund': return t('payment_status.refund');
    default: return t('payment_status.pending');
  }
};

export const getSalesChannelTextStatic = (channel: SalesChannel, t: (key: string) => string): string => {
  switch (channel) {
    case 'whatsapp': return t('sales_channel.whatsapp');
    case 'instagram': return t('sales_channel.instagram');
    case 'meta_messages': return t('sales_channel.meta_messages');
    case 'telegram': return t('sales_channel.telegram');
    case 'website': return t('sales_channel.website');
    default: return t('sales_channel.whatsapp');
  }
};

/**
 * Опции статусов для выпадающих списков с использованием i18n
 */
export const getOrderStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('filters.all_statuses') },
  { value: 'abandoned', label: t('status.abandoned') },
  { value: 'new', label: t('status.new') },
  { value: 'processing', label: t('status.processing') },
  { value: 'delivering', label: t('status.delivering') },
  { value: 'completed', label: t('status.completed') },
  { value: 'cancelled', label: t('status.cancelled') }
];

export const getPaymentStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('filters.all_payment_statuses') },
  { value: 'pending', label: t('payment_status.pending') },
  { value: 'paid', label: t('payment_status.paid') },
  { value: 'error', label: t('payment_status.error') },
  { value: 'refund', label: t('payment_status.refund') }
];

export const getSalesChannelOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('filters.all_platforms') },
  { value: 'whatsapp', label: t('sales_channel.whatsapp') },
  { value: 'instagram', label: t('sales_channel.instagram') },
  { value: 'meta_messages', label: t('sales_channel.meta_messages') },
  { value: 'telegram', label: t('sales_channel.telegram') },
  { value: 'website', label: t('sales_channel.website') }
];